import orderBy from 'lodash/orderBy';

import { BUY_HORSE_ANALYSIS_PRESSED } from 'atg/constants/Events';
import EventDispatcher from 'atg/utils/EventDispatcher';
import * as Utils from 'atg/utils/Utils';
import StickyTemplate from 'atg/vendor/StickyTemplate/1.0/StickyTemplate';
import { showManageHorseDialog } from 'shared/redux/actions';
import {
  monitor as monitorSelectors,
  user as userSelectors,
} from 'shared/redux/selectors';
import { getStore } from 'shared/redux/store';
import { formatDateDayMonth, formatDateISO } from 'shared/utils/dateUtils';
import { formatMoney, formatMoneyCompact } from 'shared/utils/money';
import { formatSentence, getText } from 'shared/utils/Text';
import { getUrl } from 'shared/utils/urls';

const $ = window.$;

const roundFixed = value => {
  const fixedValue = (Math.round(10 * value) / 10).toFixed(1);
  return Number.parseFloat(fixedValue) < 10 ? `0${fixedValue}` : fixedValue;
};

export default class HorseTemplate {
  constructor(
    startNumber,
    horseInfo,
    raceIndex,
    api,
    commentsPublishDate,
    isLoggedIn,
    callbackOptions,
  ) {
    this.startNumber = startNumber;
    this.horseInfo = horseInfo;
    this.raceIndex = raceIndex;
    this.api = api;
    this.commentsPublishDate = commentsPublishDate;
    this.isLoggedIn = isLoggedIn;
    this.callbackOptions = callbackOptions;
  }

  render() {
    const hasSubscription = userSelectors.hasSubscription(
      getStore().getState(),
    );

    const props = {
      '%id%': this.startNumber,
      '%history-th-jockey%': getText('horse-info-history-table-th-jockey'),
      '%history-th-track%': getText('horse-info-history-table-th-track'),
      '%history-th-date-run%': getText('horse-info-history-table-th-date-run'),
      '%history-th-line-distance%': getText(
        'horse-info-history-table-th-line-distance',
      ),
      '%history-th-result%': getText('horse-info-history-table-th-result'),
      '%history-th-time%': getText('horse-info-history-table-th-time'),
      '%history-th-wagon%': getText('horse-info-history-table-th-wagon'),
      '%history-th-horseshoes%': getText(
        'horse-info-history-table-th-horseshoes',
      ),
      '%history-th-odds%': getText('horse-info-history-table-th-odds'),
      '%history-th-price%': getText('horse-info-history-table-th-price'),
      '%history-th-track-conditions%': getText(
        'horse-info-history-table-th-track-conditions',
      ),
      '%race-comments-title%': getText('horse-info-race-comments-title'),
      '%analysis-th-changes%': getText('horse-info-analysis-table-th-changes'),
      '%analysis-th-conclusion%': getText(
        'horse-info-analysis-table-th-conclusion',
      ),
      '.analysis-statistics-header': getText(
        'horse-info-analysis-table-th-statistics',
      ),
      '.analysis-headtohead-header': getText(
        'horse-info-analysis-table-th-headtohead',
      ),
      '%buy-analysis-title%': getText('horse-info-buy-analysis-title'),
      '%error-fetching-analysis-title%': getText(
        'horse-info-error-fetching-analysis-title',
      ),
      '%error-fetching-analysis-message%': getText(
        'horse-info-error-fetching-analysis-message',
      ),
    };

    props['.horse-details .start-number'] =
      this.horseInfo.getStartNumber() || '';

    const horse = this.horseInfo.getHorse();

    if (horse) {
      props['.horse-details .name'] = horse.getName() || '-';
    }

    let parts;

    //  Sex/Age/Driver
    parts = [];

    const sex = horse.getSex();
    if (sex) {
      const sexCode = sex.getCode();
      if (sexCode) {
        parts.push(sexCode);
      }
    }

    const age = horse.getAge();
    if (age) {
      parts.push(age);
    }

    const jockey = this.horseInfo.getJockey();
    if (jockey) {
      parts.push(jockey.getName());
    }

    const trainer = this.horseInfo.getTrainer();
    if (trainer && (!jockey || trainer.getID() !== jockey.getID())) {
      parts.push(`(${trainer.getName()})`);
    }

    props['.horse-details .jockey'] = parts.join(' ');

    // Distance
    parts = [];

    const distance = this.horseInfo.getDistance();
    if (distance) {
      parts.push(distance);
    }

    const postPosition = this.horseInfo.getPostPosition();
    if (postPosition) {
      parts.push(postPosition);
    }
    props['.horse-details .distance-post'] = parts.join(' : ');
    this.$tpl = StickyTemplate.jq('horse-info-page', props);

    this._renderCV();
    this._renderArchiveLink();

    const $editButton = this.$tpl.find('.horse-details .edit-button .sprite');
    if (INCLUDE_HORSE_MONITORING && horse.getAtgHorseId() > 0) {
      $editButton.event('click', () => this._editButtonHandler(true));
    } else {
      $editButton.hide();
    }

    if (hasSubscription) {
      this._renderPreliminaryEquipment();
    }
    this._renderHistory();
    this._renderComments();
    this._renderAnalysis();

    return this.$tpl;
  }

  _renderPreliminaryEquipment() {
    const unsortedEquipmentTypes = Object.keys(
      this.horseInfo.getEquipment('preliminaryEquipment'),
    );

    if (unsortedEquipmentTypes.length === 0) {
      return;
    }
    const equipmentTypes = ['shoes', 'carriage', 'bridle'].filter(item =>
      unsortedEquipmentTypes.includes(item),
    );

    const props = {};
    const $preliminaryEquipment = this.$tpl.find('.preliminary-equipment');
    for (const [i, equipmentType] of equipmentTypes.entries()) {
      if (
        !this.horseInfo
          .getEquipment('preliminaryEquipment', equipmentType)
          .getName()
      ) {
        break;
      }
      const prevStartName = this.horseInfo
        .getEquipment('previousStart', equipmentType)
        ?.getName();

      const preliminaryEqipmentName = this.horseInfo
        .getEquipment('preliminaryEquipment', equipmentType)
        .getName();

      const changeSinceLastStart = prevStartName
        ? prevStartName !== preliminaryEqipmentName
        : false;

      const carriageIdMappping = {
        carriageVA: 'carriage10',
        carriageAM: 'carriage20',
        carriageHY: 'carriage30',
      };

      const prelEquipmentId =
        equipmentType +
        this.horseInfo
          .getEquipment('preliminaryEquipment', equipmentType)
          .getId();

      const equipment = carriageIdMappping[prelEquipmentId]
        ? carriageIdMappping[prelEquipmentId]
        : prelEquipmentId;

      const actualtype =
        equipmentType === 'carriage' ? 'carriages' : equipmentType;
      let equipmentStatistics = '';
      const equipments = this.horseInfo
        .getStatistics(actualtype)
        .filter(item => item !== equipment);

      for (let j = 0; j < equipments.length; j++) {
        equipmentStatistics += `${this.horseInfo
          .getStatistics(equipmentType, equipments[j])
          .getName()}: ${this.horseInfo
          .getStatistics(equipmentType, equipments[j])
          .getSummary()}${
          j < equipments.length - 1 ? ' <span class="bold-dot" >·</span> ' : ''
        }`;
      }

      props['.preliminary-equipment-type'] = `${getText(
        `horse-info-preliminary-equipment_${equipmentType}`,
      )}:`;
      props['.preliminary-equipment-info-actual span:eq(0)'] = `${
        preliminaryEqipmentName
          ? actualtype === 'bridle'
            ? getText('horse-info-preliminary-equipment')
            : ''
          : getText('horse-info-preliminary-equipment-no-info')
      }`;
      props['.preliminary-equipment-info-actual span:eq(1)'] = ` ${
        preliminaryEqipmentName ?? ''
      }`;
      const equipmentStat = this.horseInfo.getStatistics(actualtype, equipment);
      props['.preliminary-equipment-info-actual span:eq(2)'] = $(
        '<span/>',
      ).html(
        ` ${equipmentStat ? equipmentStat.getSummary() : ''} ${
          changeSinceLastStart
            ? `<span class="bold-dot" >·</span> ${getText(
                'horse-info-preliminary-equipment-info-latest',
              )}: ${
                prevStartName === 'Okänd'
                  ? getText(
                      'horse-info-preliminary-equipment-no-info-prevstart',
                    )
                  : prevStartName
              }`
            : prevStartName
              ? actualtype === 'bridle'
                ? `(${getText('horse-info-preliminary-equipment-as-latest')})`
                : ''
              : `<span class="bold-dot" >·</span>
            ${getText('horse-info-preliminary-equipment-info-latest')}:
              ${getText('horse-info-preliminary-equipment-no-info-prevstart')}`
        }`,
      );

      equipmentStatistics = $('<span/>').html(equipmentStatistics);
      props['.preliminary-equipment-info-statistics'] = equipmentStatistics;
      const $row = StickyTemplate.jq('preliminary-equipment-row', props);
      $preliminaryEquipment.append($row);

      if (
        this.horseInfo.getEquipment('previousStart', equipmentType) &&
        changeSinceLastStart
      ) {
        const $equipmentName = $preliminaryEquipment.find(
          `.preliminary-equipment-info-actual:eq(${i}) span:eq(1)`,
        );
        $equipmentName.addClass('red');
      }
    }
    $preliminaryEquipment.append(
      `<div class="preliminary-equipment-info-text" >${getText(
        'horse-info-preliminary-equipment-info-text',
      )}</div>`,
    );
  }

  _renderCV() {
    const $cv = this.$tpl.find('.horse-details .cv');
    const url = this.horseInfo.getExternalURL();

    if (url) {
      $cv.prop({
        href: url,
        target: '_blank',
      });
    } else {
      $cv.remove();
    }
  }

  _renderArchiveLink() {
    const $archive = this.$tpl.find('.horse-details .archive');
    const atgHorseId = this.horseInfo.getHorse()?.getAtgHorseId();

    if (IS_SE_SITE && atgHorseId) {
      $archive.prop({
        href: getUrl.atgVideoArchive(atgHorseId),
        target: '_blank',
      });
    } else {
      $archive.remove();
    }
  }

  _renderHistory() {
    const $tableBody = this.$tpl.find('.race-history tbody');
    $tableBody.empty();

    let numberOfRows = 5; // number of rows needed to fit all "info" rows
    let previousResults = this.horseInfo.getPreviousResults();

    // Sort by date ascending
    if (Array.isArray(previousResults)) {
      previousResults.sort(function (a, b) {
        const ma = a.getDate().moment;
        const mb = b.getDate().moment;

        if (ma.diff(mb) > 0) {
          return 1;
        } else if (ma.diff(mb) < 0) {
          return -1;
        } else {
          return 0;
        }
      });
      numberOfRows = Math.max(numberOfRows, previousResults.length);
    } else {
      previousResults = [];
    }

    let props, parts, str, trackState;

    for (let resultIndex = 0; resultIndex < numberOfRows; resultIndex++) {
      props = {};

      switch (resultIndex) {
        case 0: {
          const hometrack = this.horseInfo.getHometrack();
          if (hometrack) {
            props['.info-1'] = hometrack.getCode();
          }
          break;
        }
        case 1: {
          parts = [];

          const recordIsWinner = this.horseInfo.getRecordIsWinner();
          const recordTime = this.horseInfo.getRecordTime();
          const recordType = this.horseInfo.getRecordType();

          if (recordTime) {
            if (recordIsWinner) {
              parts.push('*');
            }

            const millis = recordTime.getTime() % (60 * 1000);
            const minutes = Math.round(millis / (1000 / 10)) / 10;
            parts.push(String(minutes).replace(/\./, ','));

            if (recordType) {
              parts.push(recordType.getCode());
            }
            props['.info-1'] = parts.join('');
          } else {
            props['.info-1'] = '-';
          }

          const earning = this.horseInfo.getEarning();
          if (earning) {
            props['.info-2'] = formatMoney(
              earning.getSum(),
              earning.getCurrency(),
            );
          }
          break;
        }
        case 2:
        case 3:
        case 4: {
          const yearlyStatsIndex = resultIndex - 2;
          let yearlyStats;

          switch (yearlyStatsIndex) {
            case 0:
              yearlyStats = this.horseInfo.getPreviousYearlyStats();
              break;
            case 1:
              yearlyStats = this.horseInfo.getCurrentYearlyStats();
              break;
            case 2:
              yearlyStats = this.horseInfo.getTotalYearlyStats();
              break;
            default:
              break;
          }

          if (yearlyStats) {
            props['.info-1'] = `${
              yearlyStats.getYear() || getText('horse-info-yearly-stats-total')
            }: ${yearlyStats.getNumberOfStarts()}`;

            props['.info-2'] = [
              yearlyStats.getNumberOfFirstPlaces(),
              yearlyStats.getNumberOfSecondPlaces(),
              yearlyStats.getNumberOfThirdPlaces(),
            ].join('-');
          }
          break;
        }
        default:
          break;
      }

      if (resultIndex < previousResults.length) {
        const previousResult = previousResults[resultIndex];

        const jockey = previousResult.getJockey();
        if (jockey) {
          props['.jockey'] = jockey.getName();
        }

        const track = previousResult.getTrack();
        if (track) {
          props['.track'] = track.getCode();
        }

        const date = previousResult.getDate();
        if (date) {
          str = Utils.formatDateCompact(date);
          const raceNumber = previousResult.getRaceNumber();
          if (raceNumber) {
            str += ` - ${raceNumber}`;
          }

          let raceType = previousResult.getRaceType();
          if (raceType) {
            raceType = raceType.toLowerCase();
            const ignoreRaceTypes = [
              'v', // vanligt lopp
              'r', // rikstoto
              'f', // ?
            ];

            if (!ignoreRaceTypes.includes(raceType)) {
              str += raceType;
            }

            // Monte
            if (previousResult.getIsMonte()) {
              str += ' - M';
            }
          }
          props['.date-race'] = str;
        }

        str = `${previousResult.getPostPosition() || '-'} / ${
          previousResult.getDistance() || '-'
        }`;

        trackState = previousResult.getTrackState();
        if (trackState) {
          const code = trackState.getCode().toLowerCase();
          const ignoreTrackStates = [
            'l', // Lätt bana
          ];

          if (!ignoreTrackStates.includes(code)) {
            str += code;
          }
        }
        props['.post-distance'] = str;

        props['.place'] =
          previousResult.getFormattedResult() === ''
            ? previousResult.getPlace()
            : previousResult.getFormattedResult();
        if (previousResult.getDisqualified()) {
          props['.place'] = 'd';
        }

        props['.time'] = previousResult.getFormattedTime();

        str = previousResult.getSulkyInfo() || '-';
        props['.wagon'] = str;

        const shoeInfo = previousResult.getShoeInfo();

        if (shoeInfo && shoeInfo.isAvailable()) {
          const horseShoe = 'c';
          const overlaySlash = '<span class="overlay-slash">/</span>';

          str = '';

          if (!shoeInfo.getForeshoes()) {
            str += overlaySlash;
          }

          str += horseShoe;

          if (!shoeInfo.getHindshoes()) {
            str += overlaySlash;
          }

          str += horseShoe;

          str = $('<span class="shoe-info"/>').html(str);
        } else {
          str = '-';
        }

        props['.shoes'] = str;

        props['.odds'] = previousResult.getOdds() || '-';

        const firstPrize = previousResult.getFirstPrize();
        props['.first-prize'] =
          firstPrize && firstPrize.getSum()
            ? formatMoneyCompact(firstPrize.getSum())
            : '-';

        trackState = previousResult.getTrackState();
        if (trackState) {
          props['.track-state'] = trackState.getText();
        }
      }

      const $row = StickyTemplate.jq(
        'horse-info-page-previous-result-row',
        props,
      );

      $tableBody.append($row);
    }
  }

  _editButtonHandler(showStartCommentInDialog) {
    const horse = this.horseInfo.getHorse();
    const atgHorseId = horse.getAtgHorseId();
    const race = this.api.raceInformation.getRace(this.raceIndex);
    const betKey = this.api.betInformation.getBetKey();
    const betType = betKey.getBetType();
    const legNumber =
      betType === 'T' ? race.getRaceNumber() : race.getLegNumber();

    this.api.loadBetKeyInfo(betKey, betKeyInfo => {
      let raceName = '';
      if (betKeyInfo) {
        const raceTime = race.getTime()?.getTime();
        const raceTimeStr = raceTime ? `, ${formatDateISO(raceTime)}` : '';
        raceName = `${betKeyInfo
          .getTrack()
          .getName()} lopp ${race.getRaceNumber()}${raceTimeStr}`;
      }
      const horseName = horse.getName();
      const roundSlug = showStartCommentInDialog ? race.getSlug() : undefined;
      const startNumber = this.startNumber;
      getStore().dispatch(
        showManageHorseDialog({
          horseName,
          raceName,
          atgHorseId,
          roundSlug,
          startNumber,
          legNumber,
        }),
      );
    });
  }

  _renderComments() {
    const horse = this.horseInfo.getHorse();
    const atgHorseId = horse.getAtgHorseId();
    const $horseCommentContainer = this.$tpl.find('.horse-comment-container');
    const horseFromMyHorses = monitorSelectors.getHorseByAtgId(
      getStore().getState(),
      { atgHorseId },
    );
    if (horseFromMyHorses?.comment) {
      $horseCommentContainer
        .find('.horse-comment-text')
        .text(horseFromMyHorses.comment);
      const $horseCommentDate = $horseCommentContainer.find(
        '.horse-comment-date',
      );
      if (horseFromMyHorses.commentDate) {
        $horseCommentDate
          .show()
          .text(formatDateDayMonth(horseFromMyHorses.commentDate));
      } else {
        $horseCommentDate.hide();
      }
      const $editStartCommentButton = $horseCommentContainer.find(
        '.edit-horse-comment-button',
      );
      $editStartCommentButton.event('click', () =>
        this._editButtonHandler(false),
      );
    } else {
      $horseCommentContainer.hide();
    }

    const $table = this.$tpl.find('.race-comments');
    const numberOfRaceComments = this.horseInfo.getNumberOfRaceComments();
    let $tableBody = this.$tpl.find('.race-comments TBODY');

    if (this.commentsPublishDate) {
      $(
        `<tr class="comment-row1"><td>${
          this.isLoggedIn ? '' : 'Logga in för att visa loppkommentarer. '
        } Loppkommentarerna publiceras ${Utils.formatDateCompact(
          this.commentsPublishDate,
        )} kl ${Utils.timeString(this.commentsPublishDate)}</td></tr>`,
      ).appendTo($tableBody);
    } else if (!this.isLoggedIn) {
      $(
        '<tr class="comment-row1"><td>Logga in för att visa loppkommentarer.</td></tr>',
      ).appendTo($tableBody);
    } else if (numberOfRaceComments === 0) {
      $table.remove();
    } else {
      $tableBody = $table.find('tbody');

      let props, parts, $row, str;

      for (
        let commentIndex = numberOfRaceComments - 1;
        commentIndex >= 0;
        commentIndex--
      ) {
        const raceComment = this.horseInfo.getRaceComment(commentIndex);

        props = {};

        props['.track-code'] = raceComment.getTrackCode();

        parts = [];

        const date = raceComment.getDate();
        if (date) {
          parts.push(Utils.formatDateCompact(date));
        }

        const raceNumber = raceComment.getRaceNumber();
        if (raceNumber) {
          parts.push(raceNumber);
        }

        props['.date-race-number'] = parts.join(' - ');

        const raceInformation = raceComment.getRace();
        if (raceInformation) {
          parts = [];
          let time;

          // första500 - första1000 - första2000 - sista500
          time = raceInformation.getFirst500();
          if (time) {
            parts.push(roundFixed(time));
          }

          time = raceInformation.getFirst1000();
          if (time) {
            parts.push(roundFixed(time));
          }

          time = raceInformation.getFirst2000();
          if (time) {
            parts.push(roundFixed(time));
          }

          time = raceInformation.getLast500();
          if (time) {
            parts.push(roundFixed(time));
          }

          props['.intermediate-times'] = parts.join(' - ');
        }

        $row = StickyTemplate.jq(
          'horse-info-race-comments-row-1',
          props,
        ).appendTo($tableBody);

        const numberOfTopHorses = raceInformation
          ? raceInformation.getNumberOfTopHorses()
          : 0;

        for (
          let topHorseIndex = 0;
          topHorseIndex < numberOfTopHorses;
          topHorseIndex++
        ) {
          const topHorse = raceInformation.getTopHorse(topHorseIndex);
          props = {};

          str = '';
          const place = topHorse.getPlace();
          if (place) {
            str += `${place}. `;
          }

          const name = topHorse.getName();
          if (name) {
            str += name;
          }

          props['.place-name'] = str;

          parts = [];
          const speed = topHorse.getSpeed();
          if (speed) {
            parts.push(roundFixed(speed));
          }

          const distance = topHorse.getDistance();
          if (distance) {
            parts.push(distance);
          }

          if (parts.length > 0) {
            props['.speed-distance'] = `(${parts.join('/')})`;
          }

          StickyTemplate.jq(
            'horse-info-race-comments-top-horse',
            props,
          ).appendTo($row);
        }

        // Make sure we have correct number of cells in the row
        if (!numberOfTopHorses || numberOfTopHorses < 3) {
          let i = numberOfTopHorses || 0;
          for (i; i < 3; i++) {
            $('<td></td>').appendTo($row);
          }
        }

        const comment = raceComment.getComment();
        let commentProps = {};

        if (comment) {
          commentProps = { '.comment': formatSentence(comment) };
        } else if (raceComment.getScratched()) {
          commentProps = {
            '.comment': `Struken: ${raceComment.getScratchedReason()}`,
          };
        } else {
          commentProps = { '.comment': 'Information saknas.' };
        }

        const $commentContainer = StickyTemplate.jq(
          'horse-info-race-comments-row-2',
          commentProps,
        ).appendTo($tableBody);

        const $addStartCommentButton = $commentContainer.find(
          '.add-start-comment-button',
        );
        const $userStartCommentContainer = $commentContainer.find(
          '.user-start-comment-container',
        );
        const startId = raceComment.getStartId();
        if (INCLUDE_HORSE_MONITORING && atgHorseId > 0 && startId > 0) {
          const $userStartComment = $commentContainer.find(
            '.user-start-comment',
          );
          const startComment = monitorSelectors.getStartComment(
            getStore().getState(),
            { startId },
          );
          let $startCommentButton;
          if (startComment) {
            $addStartCommentButton.hide();
            $userStartComment.text(startComment);
            $startCommentButton = $commentContainer.find(
              '.edit-start-comment-button',
            );
          } else {
            $userStartCommentContainer.hide();
            $startCommentButton = $addStartCommentButton;
          }
          $startCommentButton.event('click', () => {
            getStore().dispatch(
              showManageHorseDialog({
                horseName: horse.getName(),
                startId,
                raceName: `${raceComment.getTrackName()} lopp ${raceNumber}, ${formatDateISO(
                  date,
                )}`,
              }),
            );
          });
        } else {
          $addStartCommentButton.hide();
          $userStartCommentContainer.hide();
        }
      }
    }
  }

  _renderAnalysis() {
    const horseAnalysis = this.horseInfo.getHorseAnalysis();
    const horseAnalysisAvailable = this.horseInfo.isAnalysisAvailable();
    const errorFetchingHorseAnalysis =
      this.horseInfo.getErrorFetchingHorseAnalysis();

    const $errorFetchingAnalysis = this.$tpl.find('.error-fetching-analysis');
    const $horseAnalysis = this.$tpl.find('.analysis-wrapper');
    const $buyAnalysis = this.$tpl.find('.buy-analysis');

    $horseAnalysis.hide();
    $buyAnalysis.hide();
    $errorFetchingAnalysis.hide();

    if (errorFetchingHorseAnalysis) {
      $errorFetchingAnalysis.show();
    } else if (horseAnalysis != null) {
      $horseAnalysis.show();

      const changes = horseAnalysis.getChangesHTML();
      const conclusion = horseAnalysis.getConclusionHTML();
      const statistics = horseAnalysis.getStatistics();
      const headToHead = horseAnalysis.getHeadToHead();

      if (changes) {
        $horseAnalysis.find('.changes .content').html(changes);
      } else {
        $horseAnalysis.find('.changes').remove();
      }

      if (conclusion) {
        $horseAnalysis.find('.conclusion .content').html(conclusion);
      } else {
        $horseAnalysis.find('.conclusion').remove();
      }

      if (!changes && !conclusion) {
        $horseAnalysis.find('.analysis-changes-conclusion').remove();
      } else if (changes && conclusion) {
        // An additional empty column exists to make sole columns half-width.
        // If both columns have text, this is removed so they get 50% each.
        $horseAnalysis.find('.analysis-changes-conclusion .filler').remove();
      }

      // Array of stat texts
      if (statistics.length > 0) {
        const statsHtml = orderBy(statistics, 'weight', 'asc').flatMap(
          stats => {
            const { type, auto_text: autoText } = stats;

            if (autoText.includes('\n\n')) {
              return autoText
                .split('\n\n')
                .filter(line => line.trim() !== '')
                .map(
                  text =>
                    `<p${
                      type === 'horse_total_starts_missing'
                        ? ' class="red"'
                        : ''
                    }>${text}</p>`,
                );
            }

            return `<p${
              type === 'horse_total_starts_missing' ? ' class="red"' : ''
            }>${autoText}</p>`;
          },
        );
        $horseAnalysis.find('.analysis-statistics-body').html(statsHtml);
      } else {
        $horseAnalysis.find('.analysis-statistics-header').remove();
        $horseAnalysis.find('.analysis-statistics-body').remove();
      }

      // Array of head to head records from the last five starts
      // against other horses in the race
      if (Object.keys(headToHead).length > 0) {
        orderBy(
          Object.keys(headToHead).map(eventKey => {
            const { info, mutuals } = headToHead[eventKey];
            const atgHorseIds = [];
            return {
              sortProp: new Date(info.date).getTime(),
              atgHorseIds,
              ...info,
              results: orderBy(
                Object.keys(mutuals).map(resultKey => {
                  const result = mutuals[resultKey];
                  const { placement, atg_horse_id: atgHorseId } = result;
                  if (atgHorseId) {
                    atgHorseIds.push(atgHorseId);
                  }
                  let sortProp = Number.parseInt(placement, 10);
                  // Disqualified, sort last
                  if (Number.isNaN(sortProp)) {
                    sortProp = 999;
                    // Unplaced, sort second to last
                  } else if (sortProp === 0) {
                    sortProp = 99;
                  }
                  return {
                    ...result,
                    sortProp,
                  };
                }),
                'sortProp',
              ),
            };
          }),
          'sortProp',
          'desc',
        ).forEach(event => {
          const {
            date,
            atgHorseIds,
            track_name: trackName,
            track_state: trackState,
            results,
          } = event;
          const $table = StickyTemplate.jq('headtohead', {
            '%headtohead-info%': `${date}, ${trackName}, ${trackState}`,
            '%headtohead-th-placement%': getText(
              'horse-info-history-table-th-result',
            ),
            '%headtohead-th-horse%': getText(
              'horse-info-history-table-th-horse',
            ),
            '%headtohead-th-driver%': getText(
              'horse-info-history-table-th-jockey',
            ),
            '%headtohead-th-dist%': getText(
              'horse-info-history-table-th-line-distance',
            ),
            '%headtohead-th-time%': getText('horse-info-history-table-th-time'),
            '%headtohead-th-shoes%': getText(
              'horse-info-history-table-th-horseshoes',
            ),
            '%headtohead-th-odds%': getText('horse-info-history-table-th-odds'),
          });

          const $archiveLink = $table.find(
            '.analysis-headtohead-table-header-link',
          );
          if (IS_SE_SITE && atgHorseIds.length >= 2) {
            $archiveLink.prop({
              href: getUrl.atgVideoArchive(atgHorseIds),
              target: '_blank',
            });
          } else {
            $archiveLink.remove();
          }

          const $tableBody = $table.find('tbody');

          results.forEach(result => {
            const {
              current_horse: currentHorse,
              placement,
              horse_name: horseName,
              driver_name: driverName,
              start_position: startPosition,
              distance,
              speed,
              shoe_id,
              odds,
            } = result;

            const shoeId = Number.parseInt(shoe_id, 10);
            let shoes;
            if (shoeId >= 0 && shoeId <= 3) {
              const horseShoe = 'c';
              const overlaySlash = '<span class="overlay-slash">/</span>';

              switch (shoeId) {
                case 0:
                  // Barefoot
                  shoes = overlaySlash + horseShoe + overlaySlash + horseShoe;

                  break;

                case 1:
                  // Barefoot back
                  shoes = horseShoe + overlaySlash + horseShoe;

                  break;

                case 2:
                  // Barefoot front
                  shoes = overlaySlash + horseShoe + horseShoe;

                  break;

                case 3:
                  // Shoes front and back
                  shoes = horseShoe + horseShoe;

                  break;

                default:
                // Do nothing
              }

              shoes = $('<span class="shoe-info"/>').html(shoes);
            }

            const $row = StickyTemplate.jq('headtohead-row', {
              '.placement': placement,
              '.horse': horseName,
              '.driver': driverName,
              '.dist': `${startPosition} / ${distance}`,
              '.time': speed,
              '.shoes': shoes ? shoes : '-',
              '.odds':
                odds === false
                  ? getText('horse-info-history-table-ejsp')
                  : odds,
            });

            if (currentHorse) {
              $row.addClass('current-horse');
            }

            $tableBody.append($row);
          });

          $horseAnalysis.append($table);
        });
      } else {
        $horseAnalysis.find('.analysis-headtohead-header').remove();
      }
    } else if (horseAnalysisAvailable) {
      $buyAnalysis.show();

      $buyAnalysis.find('.buy-button').event('click', () => {
        EventDispatcher.emit(BUY_HORSE_ANALYSIS_PRESSED, this.callbackOptions);
      });
    }
  }
}
